// @flow

import React from "react";
import { GSGrid, TooltipDropdown } from "@components";
import { localize } from "redux-i18n";
import type { AbsenceStatistics } from "@modules/shared/reducer/employee-absences/types";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";

type Props = $Exact<{
  ...PropsFromLocalize,
  data: AbsenceStatistics,
  effectiveUserPermissions?: ?EffectiveUserPermissions,
  showAdditionalColumns?: boolean,
}>;

export class AbsenceStatisticTable extends React.Component<Props> {
  get columns() {
    return [
      {
        name: this.props.t("ABSENCE_MEASURE"),
        key: "measure",
        renderCell: ({ row }: Object) => row.measure,
        align: "left",
      },
      {
        name: this.props.t("EMPLOYEE"),
        key: "employeeStatistics",
        renderCell: ({ row }: Object) => row.employeeStatistics,
      },
      {
        name: (
          <div className="d-flex align-items-end">
            <TooltipDropdown
              id="DEPARTMENT_STATISTICS_DTO_TOOLTIP"
              title={this.props.t("DEPARTMENT_STATISTICS_DTO")}
              description={this.props.t("DEPARTMENT_STATISTICS_DTO_TOOLTIP")}
            >
              {this.props.t("DEPARTMENT_STATISTICS_DTO")}
            </TooltipDropdown>
          </div>
        ),
        key: "departmentStatisticsDto",
        renderCell: ({ row }: Object) => row.departmentStatisticsDto,
      },
      {
        name: this.props.t("DEPARTMENT_STATISTICS_PERCENT_BASED_DTO"),
        key: "departmentStatisticsPercentBasedDto",
        renderCell: ({ row }: Object) => row.departmentStatisticsPercentBasedDto,
      },
      {
        name: (
          <div className="d-flex align-items-end">
            <TooltipDropdown
              id="ORGANIZATION_STATISTICS_DTO_TOOLTIP"
              title={this.props.t("ORGANIZATION_STATISTICS_DTO")}
              description={this.props.t("ORGANIZATION_STATISTICS_DTO_TOOLTIP")}
            >
              {this.props.t("ORGANIZATION_STATISTICS_DTO")}
            </TooltipDropdown>
          </div>
        ),
        key: "organizationStatisticsDto",
        renderCell: ({ row }: Object) => row.organizationStatisticsDto,
      },
      {
        name: this.props.t("ORGANIZATION_STATISTICS_PERCENT_BASED_DTO"),
        key: "organizationStatisticsPercentBasedDto",
        renderCell: ({ row }: Object) => row.organizationStatisticsPercentBasedDto,
      },
    ];
  }

  get tableDataRows(): Array<{
    path: string,
    title: string | React$Element<any>,
  }> {
    return [
      {
        path: "totalAbsencesActive",
        title: <strong>{this.props.t("TOTAL_ABSENCES_ACTIVE")}</strong>,
      },
      {
        path: "shortTermAbsencesActive",
        title: this.props.t("SHORT_TERM_ABSENCES_ACTIVE"),
      },
      {
        path: "longTermAbsencesActive",
        title: this.props.t("LONG_TERM_ABSENCES_ACTIVE"),
      },
      {
        path: "totalAbsencesStarted",
        title: <strong>{this.props.t("TOTAL_ABSENCES_STARTED")}</strong>,
      },
      {
        path: "shortTermAbsencesStarted",
        title: this.props.t("SHORT_TERM_ABSENCES_STARTED"),
      },
      {
        path: "longTermAbsencesStarted",
        title: this.props.t("LONG_TERM_ABSENCES_STARTED"),
      },
      {
        path: "totalWorkingDaysLost",
        title: <strong>{this.props.t("TOTAL_WORKING_DAYS_LOST")}</strong>,
      },
      {
        path: "shortTermWorkingDaysLost",
        title: this.props.t("SHORT_TERM_WORKING_DAYS_LOST"),
      },
      {
        path: "longTermWorkingDaysLost",
        title: this.props.t("LONG_TERM_WORKING_DAYS_LOST"),
      },
      {
        path: "totalCalendarDaysLost",
        title: <strong>{this.props.t("TOTAL_CALENDAR_DAYS_LOST")}</strong>,
      },
      {
        path: "shortTermCalendarDaysLost",
        title: this.props.t("SHORT_TERM_CALENDAR_DAYS_LOST"),
      },
      {
        path: "longTermCalendarDaysLost",
        title: this.props.t("LONG_TERM_CALENDAR_DAYS_LOST"),
      },
      {
        path: "bradfordScore",
        title: <strong>{this.props.t("BRADFORD_SCORE")}</strong>,
      },
      {
        path: "absenceRate",
        title: this.props.t("ABSENCE_RATE"),
      },
      {
        path: "percentageOfAbsencesReportedLate",
        title: this.props.t("PERCENTAGE_OF_ABSENCES_REPORTED_LATE"),
      },
      {
        path: "percentageOfAbsencesClosedLate",
        title: this.props.t("PERCENTAGE_OF_ABSENCES_CLOSED_LATE"),
      },
      {
        path: "percentageOfFitNoteCompliance",
        title: this.props.t("PERCENTAGE_OF_FIT_NOTE_COMPLIANCE"),
      },
    ];
  }

  calcCellState = (
    displayValue: number,
    compareValue: number,
    hasArrow: boolean,
    trailingSymbol: string = "",
    colVal: boolean = false,
    compareToZero: boolean = false,
  ) => {
    let colClass = "";
    let arrowClass = "";
    if (
      (!compareToZero && displayValue < compareValue) ||
      (compareToZero && displayValue > compareValue)
    ) {
      colClass = "text-color-negative";
      arrowClass = "icon-arrow-down";
    } else if (compareValue === displayValue) {
      colClass = "text-color-coffee-d1";
      arrowClass = "icon-arrow-right";
    } else {
      colClass = "text-color-mint-d1";
      arrowClass = "icon-arrow-up";
    }

    return (
      <div>
        <span className={`${colVal ? colClass : ""} pr-2`}>
          {displayValue}
          {trailingSymbol || ""}
        </span>
        <span className={`${hasArrow ? arrowClass : ""} ${colClass}`} />
      </div>
    );
  };

  renderBoldRow = (name: string, data: Object) => {
    if (
      [
        "totalAbsencesActive",
        "totalAbsencesStarted",
        "totalWorkingDaysLost",
        "totalCalendarDaysLost",
        "bradfordScore",
      ].indexOf(name) !== -1
    ) {
      return <strong>{data}</strong>;
    }
    return data;
  };

  get tableData(): Array<*> {
    return this.tableDataRows.map(row => {
      const es = this.props.data.employeeStatistics;
      const ds = es.departmentStatistics;
      const os = es.organizationStatistics;
      const rowName = row.path;
      return {
        measure: row.title,
        employeeStatistics: this.renderBoldRow(rowName, es.employeeStatistics[rowName]),
        departmentStatisticsDto: this.renderBoldRow(
          rowName,
          this.calcCellState(
            ds.departmentStatisticsDto[rowName],
            es.employeeStatistics[rowName],
            true,
            "",
            false,
            false,
          ),
        ),
        departmentStatisticsPercentBasedDto: this.renderBoldRow(
          rowName,
          this.calcCellState(
            ds.departmentStatisticsPercentBasedDto[rowName],
            0,
            false,
            "%",
            true,
            true,
          ),
        ),
        organizationStatisticsDto: this.renderBoldRow(
          rowName,
          this.calcCellState(
            os.organizationStatisticsDto[rowName],
            es.employeeStatistics[rowName],
            true,
            "",
            false,
            false,
          ),
        ),
        organizationStatisticsPercentBasedDto: this.renderBoldRow(
          rowName,
          this.calcCellState(
            os.organizationStatisticsPercentBasedDto[rowName],
            0,
            false,
            "%",
            true,
            true,
          ),
        ),
      };
    });
  }

  render() {
    return (
      <GSGrid
        rowKeyGetter={row => {
          const key = typeof row === "string" ? row : row?.measure?.props?.children || row?.measure;
          return key;
        }}
        rows={this.tableData}
        columns={this.columns}
      />
    );
  }
}

export default localize()(AbsenceStatisticTable);
